import {
    action,
    computed,
    IReactionDisposer,
    makeObservable,
    observable,
    reaction,
    runInAction,
    toJS,
} from 'mobx';
import difference from 'lodash/difference';
import differenceWith from 'lodash/differenceWith';

import AuthSettingsStore from '../AuthSettingsStore';
import UsersListStore from '../UsersListStore';
import UserStore from '../UserStore';
import { UsersPhonesStore } from '../UsersPhonesStore';
import { LinksLoader } from './LinksLoader';
import {
    Recipient,
    RecipientRoles,
    RecipientType,
    SharedUser,
    StorageProvider,
} from '@/types/types';
import {
    BulkShareRequestPayload,
    FilesToSharePayload,
    FileShareUsersDict,
    LinkPreloadMode,
    LinksPreloadingConfig,
    RecipientsShareHints,
    RecipientToDisplay,
    ShareLink,
    ShareLinksDict,
    SharedUserToDisplay,
    UserEmailsResponse,
    UserSharedOwner,
} from './interfaces';
import { captureErrorForSentry, checkIs404 } from '../../components/utils';
import {
    BASEURL,
    ENDPOINTS,
    getFilePermissions,
} from '../../api';
import appConfig from '../../config/env';
import {
    BEUserToDisplayUser,
    compareActions,
    displayUserToBE,
    prepareRecipientsPayload,
    roleToPrimaryData,
} from './helpers';
import {
    EMPTY_RECIPIENT,
    INIT_SHARE_LINK_PARTIAL,
    getInviteAsDefault,
    SHARING_HINT_TIMEOUT,
    RECIPIENTS_HINTS_LIST,
} from './constants';
import type { RecipientPasswordInfo, SetPasswordResult } from './passwordTypes.d.ts';

export {
    BEUserToDisplayUser,
    compareActions,
    displayUserToBE,
    RECIPIENTS_HINTS_LIST,
    roleToPrimaryData,
};
export type { SharedUserToDisplay };

class SharedUsersStore {
    constructor(
        authSettingsStore: AuthSettingsStore,
        usersListStore: UsersListStore,
        usersPhonesStore: UsersPhonesStore,
        userStore: UserStore,
    ) {
        this.authSettingsStore = authSettingsStore;
        this.usersPhonesStore = usersPhonesStore;
        this.usersListStore = usersListStore;
        this.userStore = userStore;
        this.linksLoader = new LinksLoader(authSettingsStore);
        makeObservable(this);
    }

    @observable hasError = false;

    @observable sharedUsers: SharedUser[] = [];

    @observable recipientsList: Recipient[] = [];

    @observable emailsList: string[] = [];

    @observable currentInputEmail = '';

    @observable inviteAs: RecipientRoles = getInviteAsDefault();

    @observable shareHint: RecipientsShareHints;

    @observable shareLinks: ShareLinksDict = new Map<string, ShareLink>();

    @observable fileSharedUsers: FileShareUsersDict = new Map<string, SharedUser[]>();

    @observable recentlySharedFileIds: Set<string> = new Set<string>();

    @observable filesIds: string[];

    // TODO: add cache invalidation here
    @observable usersSharedOwnersList: UserSharedOwner[] = [];

    @observable notifyRecipients = appConfig.DEFAULT_NOTIFY_RECIPIENTS ?? true;

    @observable customMessage = '';

    @observable encryptionToggle = true;

    @observable loadingGroupMembersModal = false;

    @observable messageId = '';

    @observable recipientsPasswordsMap: Map<string, RecipientPasswordInfo> = new Map();

    private readonly authSettingsStore: AuthSettingsStore;

    private readonly usersPhonesStore: UsersPhonesStore;

    private readonly usersListStore: UsersListStore;

    private readonly userStore: UserStore;

    private readonly linksLoader: LinksLoader;

    private shareHintTimeout: NodeJS.Timeout;

    private recipientsReactionDisposer: IReactionDisposer;

    private singleRecipientReactionDisposer: IReactionDisposer;

    private filesIdsReactionDisposer: IReactionDisposer;

    private linkPreloadMode: LinkPreloadMode;

    private isPreloadFlowActive = false;

    private origin: StorageProvider;

    @computed
    get sharedUsersToDisplay(): SharedUserToDisplay[] {
        return this.sharedUsers.map<SharedUserToDisplay>(BEUserToDisplayUser);
    }

    @computed
    get recipientsToDisplay(): RecipientToDisplay[] {
        return this.recipientsList.map<RecipientToDisplay>((recipient) => {
            const phoneInfo = this.usersPhonesStore.getUserPhone(recipient.email);
            return {
                ...recipient,
                phoneInfo,
            };
        });
    }

    @computed
    get hasShareError(): boolean {
        return this.hasError;
    }

    @computed
    get isOnlyOneRecipient(): boolean {
        return this.recipientsList.length === 1;
    }

    @computed
    get hasRecipients(): boolean {
        return !!this.recipientsList.length;
    }

    @computed
    get hasMissingPassword(): boolean {
        const { userGroupsList } = this.usersListStore;

        return this.recipientsList.some(({ type, email, id }) => {
            if (type === 'user') {
                const info = this.recipientsPasswordsMap.get(email);
                return !!info && !info.isChecking && info.hasPassword !== true;
            }

            const group = userGroupsList.find(({ groupId }) => groupId === id);
            if (!group) {
                return false;
            }

            return (group.members || []).some(({ email: memberEmail }) => {
                const info = this.recipientsPasswordsMap.get(memberEmail);
                return !!info && !info.isChecking && info.hasPassword !== true;
            });
        });
    }

    @computed
    get hasSearchingPassword(): boolean {
        const { userGroupsList } = this.usersListStore;

        return this.recipientsList.some(({ type, email, id }) => {
            if (type === 'user') {
                const info = this.recipientsPasswordsMap.get(email);
                return !info || info.isChecking;
            }

            const group = userGroupsList.find(({ groupId }) => groupId === id);
            if (!group) {
                // We don't yet know group members – treat as "searching".
                return true;
            }

            return (group.members || []).some(({ email: memberEmail }) => {
                const info = this.recipientsPasswordsMap.get(memberEmail);
                return !info || info.isChecking;
            });
        });
    }

    @computed
    get singleRecipientIdentity(): string {
        if (this.recipientsList.length === 1) {
            const firstRecipient = this.recipientsList[0];
            return firstRecipient.type === 'user' ? firstRecipient.email : firstRecipient.id;
        }
        return EMPTY_RECIPIENT;
    }

    @computed
    get recipientsEmails(): string[] {
        return this.recipientsList.map<string>(({
            email,
            type,
            id,
        }) => (type === 'user' ? email : id));
    }

    @computed
    get shareLinksToJS(): ShareLinksDict {
        return toJS(this.shareLinks);
    }

    @action
    private setShareHint(value: RecipientsShareHints): void {
        this.shareHint = value;
    }

    clearShareHint = (): void => {
        clearTimeout(this.shareHintTimeout);
        this.setShareHint(null);
    };

    @action
    private setRecipientPasswordInfo = (email: string, info: RecipientPasswordInfo): void => {
        this.recipientsPasswordsMap.set(email, info);
    };

    @action
    private deleteRecipientPasswordInfo = (email: string): void => {
        this.recipientsPasswordsMap.delete(email);
    };

    addShareHint = (newValue: RecipientsShareHints): void => {
        clearTimeout(this.shareHintTimeout);
        this.setShareHint(newValue);
        this.shareHintTimeout = setTimeout(
            () => this.setShareHint(null),
            SHARING_HINT_TIMEOUT,
        );
    };

    @action
    setCurrentInputEmail = (newValue: string): void => {
        this.currentInputEmail = newValue;
    };

    @action
    setMessageId = (value: string): void => {
        this.messageId = value;
    };

    @action
    setInviteAs = (newValue: RecipientRoles): void => {
        this.inviteAs = newValue;
    };

    @action
    setEmailsList = (emails: string[]): void => {
        this.emailsList = emails;
    };

    @action
    setOwnersList = (ownersList: UserSharedOwner[]): void => {
        this.usersSharedOwnersList = ownersList;
    };

    @action
    setNotifyRecipients = (value: boolean): void => {
        this.notifyRecipients = value;
    };

    @action
    setCustomMessage = (value: string): void => {
        this.customMessage = value;
    };

    @action
    setEncryptionToggle = (value: boolean): void => {
        this.encryptionToggle = value;
    };

    @action
    setLoadingGroupMembersModal = (value: boolean): void => {
        this.loadingGroupMembersModal = value;
    };

    @action
    setFilesIds = (value: string[]): void => {
        this.filesIds = value;
    };

    fetchEmailsList = async (): Promise<void> => {
        try {
            const { API } = this.authSettingsStore;
            const { emails }: UserEmailsResponse = await API.get(BASEURL.backend(), ENDPOINTS.getEmails(), {});
            if (!emails) {
                throw Error('Incorrect response from ENDPOINTS.getEmails(): Expected { emails: string[] }');
            }
            const nonEmptyEmails = emails.filter(Boolean);
            this.setEmailsList(nonEmptyEmails);
        } catch (error) {
            captureErrorForSentry(error, 'SharedUsersStore.fetchEmailsList');
            console.log('could not fetch emails', error);
        }
    };

    tryBulkSavePhonesSettings = async (): Promise<boolean> => (
        this.usersPhonesStore.tryBulkSavePhones(this.sharedUsers.map<string>(({ email }) => email))
    );

    @action
    setSharedUsers = (sharedUsers: SharedUser[], fileId: string): void => {
        this.sharedUsers = sharedUsers;
        this.setFileSharedUsersList(fileId, sharedUsers);
    };

    partialUpdateRecipient = (email: string, dataForUpdate: Partial<Recipient>): void => {
        const newRecipientsList = this.recipientsList.map((recipient) => {
            let newRecipient: Recipient = recipient;
            if (newRecipient.email === email) {
                newRecipient = { ...newRecipient, ...dataForUpdate };
            }
            return newRecipient;
        });

        this.setRecipientsList(newRecipientsList);
    };

    removeRecipient = (targetRecipientId: string): void => {
        const filteredList = this.recipientsList.filter(({ id }) => targetRecipientId !== id);
        this.setRecipientsList(filteredList);
    };

    @action
    private setRecipientsList(recipientsList: Recipient[]): void {
        this.recipientsList = recipientsList;
    }

    addRecipients = (newRecipients: Recipient[]): void => {
        this.setRecipientsList([...this.recipientsList, ...newRecipients]);
    };

    private checkRecipientPassword = async (email: string): Promise<void> => {
        const { API } = this.authSettingsStore;
        const currentInfo = this.recipientsPasswordsMap.get(email);
        this.setRecipientPasswordInfo(email, {
            isChecking: true,
            hasPassword: currentInfo?.hasPassword ?? null,
            currentPassword: currentInfo?.currentPassword ?? null,
            maskedPassword: currentInfo?.maskedPassword ?? null,
        });
        try {
            const response = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getUserPassword(email),
                {},
            );
            const passwordValue = response?.password;
            const hasPassword = typeof passwordValue === 'string' && passwordValue.length > 0;
            // Backend returns masked password (e.g., "****1234")
            const maskedPassword = hasPassword ? passwordValue : null;
            this.setRecipientPasswordInfo(email, {
                isChecking: false,
                hasPassword,
                currentPassword: null,
                maskedPassword,
            });
        } catch (error) {
            if (!checkIs404(error)) {
                captureErrorForSentry(error, 'SharedUsersStore.checkRecipientPassword');
            }
            this.setRecipientPasswordInfo(email, {
                isChecking: false,
                hasPassword: false,
                currentPassword: null,
                maskedPassword: null,
            });
        }
    };

    getRecipientPasswordInfo = (email: string): RecipientPasswordInfo => this.recipientsPasswordsMap.get(email) || {
        isChecking: false,
        hasPassword: null,
        currentPassword: null,
        maskedPassword: null,
    };

    setRecipientPassword = async (email: string, password: string): Promise<SetPasswordResult> => {
        const { API } = this.authSettingsStore;
        try {
            await API.post(
                BASEURL.backend(),
                ENDPOINTS.setUserPassword(email),
                { body: { password } },
            );
            // Mask the password for display (show last 1 char)
            const maskedPassword = password.length > 1
                ? `${'*'.repeat(password.length - 1)}${password.slice(-1)}`
                : '*'.repeat(password.length);
            this.setRecipientPasswordInfo(email, {
                isChecking: false,
                hasPassword: true,
                currentPassword: password,
                maskedPassword,
            });
            return { success: true };
        } catch (error) {
            captureErrorForSentry(error, 'SharedUsersStore.setRecipientPassword');
            const errorMessage = (error as any)?.response?.data?.message
                || (error as any)?.message
                || '';
            return { success: false, errorMessage };
        }
    };

    checkGroupMembersPasswords = async (targetGroupId: string): Promise<void> => {
        const { userGroupsList, fetchUsersCustomGroup } = this.usersListStore;
        if (!userGroupsList.some(({ groupId }) => groupId === targetGroupId)) {
            await fetchUsersCustomGroup(targetGroupId);
        }
        const groupIndex = this.usersListStore.userGroupsList
            .findIndex(({ groupId }) => groupId === targetGroupId);
        if (groupIndex !== -1) {
            const membersEmails = (this.usersListStore.userGroupsList[groupIndex].members || [])
                .map(({ email }) => email);
            await Promise.all(
                membersEmails.map((email) => this.checkRecipientPassword(email)),
            );
        }
    };

    fetchSharedUsers = async (
        fileId: string, shouldFetchPhones = false, shouldFetchPasswords = false,
    ): Promise<void> => {
        try {
            this.setHasShareError(false);
            await this.getFilePermissionsAndSaveInStore(fileId);
            const userList = this.fileSharedUsers.get(fileId);
            const detailFetches: Promise<void>[] = [];
            if (shouldFetchPhones && userList) {
                detailFetches.push(this.usersPhonesStore.fetchSharedUsersPhones(userList));
            }
            if (shouldFetchPasswords && userList) {
                userList
                    .filter(({ email }) => email)
                    .forEach(({ email }) => detailFetches.push(this.checkRecipientPassword(email)));
            }
            await Promise.all(detailFetches);
        } catch (error) {
            captureErrorForSentry(error, 'SharedUsersStore.fetchSharedUsers');
            this.setHasShareError(true);
        }
    };

    getFilePermissionsAndSaveInStore = async (fileId: string): Promise<void> => {
        const { API } = this.authSettingsStore;
        const { users } = await getFilePermissions(API, fileId);
        this.setSharedUsers(users, fileId);
    };

    /**
     * Reconcile fileSharedUsers with the BE after a just-completed share. The
     * share endpoint returns before getFilePermissions is consistent, so the
     * initial reads can come back empty or partial even though the share
     * succeeded. We poll until the BE reports at least as many recipients as
     * we seeded optimistically; if it never catches up, we keep the optimistic
     * data so the UI doesn't silently drop just-shared users.
     */
    reconcileSharedUsersAfterShare = async (
        fileId: string,
        maxAttempts = 5,
        delayMs = 3_000,
    ): Promise<void> => {
        const { API } = this.authSettingsStore;
        const expectedCount = this.fileSharedUsers.get(fileId)?.length ?? 0;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            try {
                const { users } = await getFilePermissions(API, fileId);
                if ((users?.length ?? 0) >= expectedCount && expectedCount > 0) {
                    this.setSharedUsers(users, fileId);
                    return;
                }
                if (!expectedCount && users?.length) {
                    this.setSharedUsers(users, fileId);
                    return;
                }
            } catch (error) {
                captureErrorForSentry(error, 'SharedUsersStore.reconcileSharedUsersAfterShare');
                return;
            }
            if (attempt < maxAttempts - 1) {
                await new Promise<void>((resolve) => { setTimeout(resolve, delayMs); });
            }
        }
    };

    getFileSharedUsersList = (fileId: string): SharedUser[] => this.fileSharedUsers.get(fileId);

    @action
    setHasShareError = (val: boolean): void => {
        this.hasError = val;
    };

    @action
    setShareLinks = (newValue: ShareLinksDict): void => {
        this.shareLinks = newValue;
    };

    @action
    setFileSharedUsersList = (fileId: string, userList: SharedUser[]): void => {
        this.fileSharedUsers.set(fileId, userList);
    };

    @action
    markRecentlyShared = (fileId: string): void => {
        if (fileId) {
            this.recentlySharedFileIds.add(fileId);
        }
    };

    @action
    consumeRecentlyShared = (fileId: string): boolean => {
        if (this.recentlySharedFileIds.has(fileId)) {
            this.recentlySharedFileIds.delete(fileId);
            return true;
        }
        return false;
    };

    @action
    setLinkForRecipient = (recipientEmail: string, newValue: ShareLink): void => {
        this.shareLinks.set(recipientEmail, newValue);
    };

    @action
    setSingleRecipientLink = (recipientEmail: string, newValue: ShareLink): void => {
        this.shareLinks.clear();
        this.shareLinks.set(recipientEmail, newValue);
    };

    private async tryLoadLink(recipientIdentity: string, recipientType: RecipientType): Promise<void> {
        const linkData = this.shareLinks.get(recipientIdentity);
        if (linkData) {
            this.setLinkForRecipient(recipientIdentity, { ...INIT_SHARE_LINK_PARTIAL, recipientType });
            const link = await this.linksLoader.createLink({
                filesIds: this.filesIds,
                recipientIdentity,
                recipientType,
                messageId: this.messageId,
                origin: this.origin,
            });
            const currentLinkData = this.shareLinks.get(recipientIdentity);
            if (currentLinkData) {
                this.setLinkForRecipient(recipientIdentity, { isLoading: false, link, recipientType });
            }
            this.setMessageId('');
        } else {
            console.error('Link data does not exists: recipient is', recipientIdentity);
        }
    }

    private setInitLinks(preloadMode: LinkPreloadMode): void {
        const linksMap: ShareLinksDict = new Map<string, ShareLink>();
        if (preloadMode === 'general') {
            const singleRecipient = this.singleRecipientIdentity !== EMPTY_RECIPIENT ? this.recipientsList[0] : null;
            const singleRecipientType = singleRecipient?.type || null;
            linksMap.set(
                this.singleRecipientIdentity, { ...INIT_SHARE_LINK_PARTIAL, recipientType: singleRecipientType },
            );
        } else if (preloadMode === 'allCover') {
            this.recipientsList.forEach(({ email, type, id }) => {
                const identity = type === 'user' ? email : id;
                linksMap.set(identity, { ...INIT_SHARE_LINK_PARTIAL, recipientType: type });
            });
            linksMap.set(
                EMPTY_RECIPIENT, { ...INIT_SHARE_LINK_PARTIAL, recipientType: null },
            );
        } else {
            this.recipientsList.forEach(({ email, type, id }) => {
                const identity = type === 'user' ? email : id;
                linksMap.set(identity, { ...INIT_SHARE_LINK_PARTIAL, recipientType: type });
            });
        }
        this.setShareLinks(linksMap);
    }

    forceRequestLink = async (recipientIdentity: string): Promise<void> => {
        const recipientType = this.recipientsList.find(
            ({ id, email }) => id === recipientIdentity || email === recipientIdentity,
        )?.type;
        if (!this.shareLinks.has(recipientIdentity)) {
            this.setLinkForRecipient(recipientIdentity, { ...INIT_SHARE_LINK_PARTIAL, recipientType });
        }
        await this.tryLoadLink(recipientIdentity, recipientType);
    };

    private setIsPreloadFlowActive(newValue: boolean): void {
        this.isPreloadFlowActive = newValue;
    }

    private onSingleRecipientChange = (recipientIdentity: string): void => {
        this.linksLoader.cancelAll();
        const recipientType = recipientIdentity !== EMPTY_RECIPIENT
            ? this.recipientsList[0]?.type
            : null;
        this.setSingleRecipientLink(recipientIdentity, { ...INIT_SHARE_LINK_PARTIAL, recipientType });
        this.tryLoadLink(recipientIdentity, recipientType);
    };

    private runLinksReaction(mode: LinkPreloadMode): void {
        this.linkPreloadMode = mode;
        if (mode === 'general') {
            this.singleRecipientReactionDisposer = reaction<string>(
                () => this.singleRecipientIdentity,
                this.onSingleRecipientChange,
            );
        } else {
            this.runRecipientsReaction();
        }
    }

    private runFilesIdsReaction(): void {
        this.filesIdsReactionDisposer = reaction<string[]>(
            () => this.filesIds,
            this.onFilesIdsChange,
        );
    }

    private onFilesIdsChange = (currentList: string[], prevList: string[]): void => {
        const hasDiff = !!difference(currentList, prevList).length;
        if (hasDiff) {
            this.linksLoader.cancelAll();
            runInAction(() => {
                this.shareLinks.forEach((linkData, recipientEmail) => {
                    this.shareLinks.set(recipientEmail, {
                        ...INIT_SHARE_LINK_PARTIAL, recipientType: linkData.recipientType,
                    });
                    this.tryLoadLink(recipientEmail, linkData.recipientType);
                });
            });
        }
    };

    private handleDeletedRecipients(deletedRecipients: Recipient[]): void {
        runInAction(() => {
            deletedRecipients.forEach(({ type, email: recipientEmail, id }) => {
                const isUser = type === 'user';
                if (this.linkPreloadMode === 'personal') {
                    const identity = isUser ? recipientEmail : id;
                    this.linksLoader.cancelRequest(identity);
                    this.shareLinks.delete(identity);
                }

                if (isUser) {
                    this.usersPhonesStore.deletePhone(recipientEmail);
                    this.deleteRecipientPasswordInfo(recipientEmail);
                } else {
                    const group = this.usersListStore.userGroupsList.find(({ groupId }) => groupId === id);
                    (group?.members || []).forEach(({ email }) => {
                        this.usersPhonesStore.deletePhone(email);
                        this.deleteRecipientPasswordInfo(email);
                    });
                }
            });
        });
    }

    handleAddedRecipients(addedRecipients: Recipient[]): void {
        addedRecipients.forEach(({ email, id, type }) => {
            const isUser = type === 'user';
            if (this.linkPreloadMode === 'personal') {
                const identity = isUser ? email : id;
                this.setLinkForRecipient(identity, { ...INIT_SHARE_LINK_PARTIAL, recipientType: type });
                this.tryLoadLink(identity, type);
            }
            if (isUser) {
                this.usersPhonesStore.fillPhones(email);
                this.checkRecipientPassword(email);
            } else {
                this.usersPhonesStore.fetchGroupMembersPhones(id);
                this.checkGroupMembersPasswords(id);
            }
        });
    }

    private onRecipientsListChange = (newList: Recipient[], prevList: Recipient[]): void => {
        const addedRecipients: Recipient[] = differenceWith(
            newList,
            prevList,
            ({ id: newId }, { id }) => newId === id,
        );
        const deletedRecipients: Recipient[] = differenceWith(
            prevList,
            newList,
            ({ id: newId }, { id }) => newId === id,
        );
        this.handleDeletedRecipients(deletedRecipients);
        this.handleAddedRecipients(addedRecipients);
    };

    private cancelAllLinksRequests(): void {
        this.linksLoader.cancelAll();
    }

    @action
    clearSharedUsers = (): void => {
        this.hasError = false;
        this.sharedUsers.forEach(({ email }) => {
            this.usersPhonesStore.deletePhone(email);
            this.deleteRecipientPasswordInfo(email);
        });
        this.sharedUsers = [];
        this.fileSharedUsers.clear();
    };

    @action
    resetRecipientsInput = (emailsToClean?: string[]): void => {
        if (emailsToClean) {
            emailsToClean.forEach((email) => this.recipientsPasswordsMap.delete(email));
        }
        this.recipientsList = [];
        this.currentInputEmail = '';
    };

    @action
    clearRecipients = (): void => {
        this.recipientsList = [];
        this.currentInputEmail = '';
        this.inviteAs = getInviteAsDefault();
        this.notifyRecipients = appConfig.DEFAULT_NOTIFY_RECIPIENTS ?? true;
        this.setCustomMessage('');
        this.usersPhonesStore.clearPhones();
        this.recipientsPasswordsMap.clear();
        this.disposeReaction('recipientsReactionDisposer');
    };

    @action
    clearLinks = (): void => {
        this.filesIds = [];
        this.shareLinks = new Map<string, ShareLink>();
        this.origin = null;
    };

    finishRecipientsFlow = (): void => {
        this.cancelLinksPreloading();
        this.clearRecipients();
    };

    private disposeReaction(disposerName: 'recipientsReactionDisposer'
    | 'filesIdsReactionDisposer'
    | 'singleRecipientReactionDisposer'): void {
        this[disposerName]?.();
        this[disposerName] = null;
    }

    cancelLinksPreloading = (): void => {
        if (this.isPreloadFlowActive) {
            this.setIsPreloadFlowActive(false);
            this.disposeReaction('recipientsReactionDisposer');
            this.disposeReaction('singleRecipientReactionDisposer');
            this.disposeReaction('filesIdsReactionDisposer');
            this.cancelAllLinksRequests();
            this.clearLinks();
            this.linkPreloadMode = null;
        }
    };

    runRecipientsReaction = (): void => {
        if (!this.recipientsReactionDisposer) {
            this.recipientsReactionDisposer = reaction<Recipient[]>(
                () => this.recipientsList,
                this.onRecipientsListChange,
            );
            // Handle existing recipients that may not have password info fetched yet
            this.checkExistingRecipientsPasswords();
        }
    };

    private checkExistingRecipientsPasswords = (): void => {
        this.recipientsList.forEach(({ email, id, type }) => {
            const isUser = type === 'user';
            if (isUser) {
                const existingInfo = this.recipientsPasswordsMap.get(email);
                // Only fetch if not already fetched or currently fetching
                if (!existingInfo || (existingInfo.hasPassword === null && !existingInfo.isChecking)) {
                    this.checkRecipientPassword(email);
                }
            } else {
                this.checkGroupMembersPasswords(id);
            }
        });
    };

    startLinksPreloading = ({
        filesIds = [],
        origin,
        mode,
    }: LinksPreloadingConfig): void => {
        if (!this.isPreloadFlowActive) {
            this.origin = origin;
            this.setIsPreloadFlowActive(true);
            this.setInitLinks(mode);
            this.runLinksReaction(mode);
            this.runFilesIdsReaction();
            this.setFilesIds(filesIds);
        } else {
            console.error('links preloading is already active');
        }
    };

    fetchOwners = async (): Promise<void> => {
        const { API } = this.authSettingsStore;
        const ownersList = await API.get(BASEURL.backend(), ENDPOINTS.getOwnersList(), {});
        this.setOwnersList(ownersList);
    };

    bulkShareFiles = async ({
        files,
        origin,
        sendNotification = true,
    }: FilesToSharePayload): Promise<void> => {
        const {
            authSettingsStore: { API },
            notifyRecipients,
            messageId,
            encryptionToggle,
            recipientsToDisplay,
        } = this;
        const recipients = prepareRecipientsPayload(recipientsToDisplay);
        const requestBody: BulkShareRequestPayload = {
            files,
            notify_recipients: notifyRecipients,
            message_id: messageId,
            protect_message: encryptionToggle,
            sendNotification,
            ...recipients,
        };
        if (origin) {
            requestBody.origin = origin;
        }
        await API.post(BASEURL.backend(), ENDPOINTS.shareFiles(), { body: requestBody });
    };

    createNew = (): SharedUsersStore => (
        new SharedUsersStore(this.authSettingsStore, this.usersListStore, this.usersPhonesStore, this.userStore)
    );
}

export default SharedUsersStore;
